import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Spinner } from '../../../components/ui/Spinner';
import type { PublicHostedReportStatus } from '../../../types/hostedReport';
import { REPORT_SANDBOX_ATTRIBUTE } from '../../../lib/hostedReportSandbox';

// Tools → HTML Hosting — the PUBLIC viewer at /r/:token, rendered OUTSIDE
// SignedInGuard + AppShell so a client never sees the agency app chrome.
//
// ⚠️ THIS PAGE IS OURS — our origin, our components. It resolves the link's
// state, and hosts the password gate when there is one. The REPORT ITSELF is
// never rendered here: it is embedded in an IFRAME pointed at the API's
// /content route, which serves it under a `Content-Security-Policy: sandbox …`
// header in a unique opaque origin. That separation is the whole security
// design — see server/modules/hostedReports/publicRouter.ts, and
// src/lib/hostedReportSandbox.ts for the policy itself. (The policy is named
// rather than quoted here on purpose: a comment carrying a copy of a value is
// one more place for it to go stale.)
//
// Concretely: do NOT "simplify" this by fetching the HTML here and injecting it
// with dangerouslySetInnerHTML. That would run the report's scripts in OUR
// origin, with access to the localStorage where Firebase Auth keeps a signed-in
// staff member's session — which is exactly what the sandbox exists to prevent.

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 text-center shadow-sm">
        {children}
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return 'its expiry date';
  return new Date(ms).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function HostedReportPage() {
  const { token = '' } = useParams<{ token: string }>();
  const [status, setStatus] = useState<PublicHostedReportStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const [unlocking, setUnlocking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/public/hosted-reports/${encodeURIComponent(token)}`);
      setStatus((await res.json()) as PublicHostedReportStatus);
    } catch {
      setStatus({ status: 'not_found' });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (status && 'title' in status) document.title = status.title;
  }, [status]);

  async function unlock(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setUnlocking(true);
    setUnlockError(null);
    try {
      const res = await fetch(
        `/api/public/hosted-reports/${encodeURIComponent(token)}/unlock`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password }),
        },
      );
      const body = (await res.json()) as { status?: string; retryAfterSeconds?: number };
      if (res.ok && body.status === 'ok') {
        setPassword('');
        await load();
        return;
      }
      if (res.status === 429) {
        const mins = Math.ceil((body.retryAfterSeconds ?? 900) / 60);
        setUnlockError(`Too many attempts. Try again in about ${mins} minutes.`);
        return;
      }
      setUnlockError('That password is not right.');
    } catch {
      setUnlockError('Something went wrong. Try again.');
    } finally {
      setUnlocking(false);
    }
  }

  if (loading) {
    return (
      <Shell>
        <Spinner />
      </Shell>
    );
  }

  if (!status || status.status === 'not_found') {
    return (
      <Shell>
        <h1 className="text-lg font-semibold text-slate-900">Report not found</h1>
        <p className="mt-2 text-sm text-slate-600">
          This link doesn’t match a report. Check you have the whole URL, or ask
          whoever sent it for a new one.
        </p>
      </Shell>
    );
  }

  if (status.status === 'revoked') {
    return (
      <Shell>
        <h1 className="text-lg font-semibold text-slate-900">This link has been revoked</h1>
        <p className="mt-2 text-sm text-slate-600">
          The report is no longer available at this address. Get in touch with your
          account manager if you still need it.
        </p>
      </Shell>
    );
  }

  if (status.status === 'expired') {
    return (
      <Shell>
        <h1 className="text-lg font-semibold text-slate-900">This link has expired</h1>
        <p className="mt-2 text-sm text-slate-600">
          It stopped working on {formatDate(status.expiresAt)}. Your account manager
          can send you a fresh link.
        </p>
      </Shell>
    );
  }

  if (status.status === 'password_required') {
    return (
      <Shell>
        <Lock className="mx-auto h-6 w-6 text-slate-400" aria-hidden />
        <h1 className="mt-3 text-lg font-semibold text-slate-900">
          This report is password protected
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          Enter the password you were given to open it.
        </p>
        <form onSubmit={(e) => void unlock(e)} className="mt-4 space-y-3">
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            aria-label="Password"
            autoFocus
          />
          {unlockError && <p className="text-sm text-destructive">{unlockError}</p>}
          <Button type="submit" className="w-full" disabled={unlocking || !password}>
            {unlocking ? 'Checking…' : 'Open report'}
          </Button>
        </form>
      </Shell>
    );
  }

  // status.status === 'ok' — the report itself, sandboxed.
  return (
    <div className="h-[100dvh] w-full bg-white">
      <iframe
        // ⚠️⚠️ THE ATTRIBUTE IS NOT THE ENFORCEMENT. The server sends a
        // `Content-Security-Policy: sandbox …` header with this document, and
        // THAT is what puts it in an opaque origin; this attribute is
        // belt-and-braces on top of it. The effective restriction is the UNION
        // of the two, so relaxing this alone grants NOTHING and looks like a
        // browser bug — the two must move together, which is why the token list
        // is DERIVED from the same constant the header is built from rather
        // than written out here. See src/lib/hostedReportSandbox.ts.
        sandbox={REPORT_SANDBOX_ATTRIBUTE}
        src={`/api/public/hosted-reports/${encodeURIComponent(token)}/content`}
        title={status.title}
        className="h-full w-full border-0"
      />
    </div>
  );
}
